import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../config';
import { LogisticsInvoice } from '../../types';
import { rebuildRevenuesLedger } from './revenuesLedgers';

// Invoices Collection
export const addInvoice = async (invoice: Omit<LogisticsInvoice, 'id' | 'createdAt'>): Promise<string> => {
  try {
    
    // Check if invoice number already exists (exclude drafts - they can be overwritten)
    const existingInvoice = await getInvoiceByNumber(invoice.invoiceNumber);
    if (existingInvoice && existingInvoice.status !== 'draft') {
      throw new Error(`Invoice with number ${invoice.invoiceNumber} already exists`);
    }
    
    // Use invoice number as the document ID
    const docRef = doc(db, 'invoices', invoice.invoiceNumber);
    await setDoc(docRef, {
      ...invoice,
      createdAt: serverTimestamp()
    });
    
    // Note: Ledger will be updated when invoice is approved, not when created
    
    return invoice.invoiceNumber;
  } catch (error) {
    throw new Error(`Error adding invoice: ${error}`);
  }
};

export const updateInvoiceStatus = async (invoiceId: string, status: LogisticsInvoice['status'], approvedBy?: string, invoiceData?: LogisticsInvoice): Promise<void> => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    const updateData: any = { 
      status, 
      approvedAt: serverTimestamp() 
    };
    
    if (approvedBy) {
      updateData.approvedBy = approvedBy;
    }
    
    // If invoice data is provided, update the entire invoice
    if (invoiceData) {
      const { id, status: _, approvedAt: __, ...dataToUpdate } = invoiceData; // Exclude status, id, and approvedAt from invoiceData
      Object.assign(updateData, dataToUpdate);
    }
    
    await updateDoc(invoiceRef, updateData);
    
    // Rebuild the revenues ledger when invoice status changes
    const invoice = await getInvoiceById(invoiceId);
    if (invoice) {
      await rebuildRevenuesLedger(invoice.clientId);
    }
  } catch (error) {
    throw new Error(`Error updating invoice: ${error}`);
  }
};

// Update invoice fields (for amendments/edits)
export const updateInvoice = async (
  invoiceId: string,
  updates: Partial<Omit<LogisticsInvoice, 'id' | 'createdAt' | 'approvedAt'>>
): Promise<void> => {
  try {
    const invoiceRef = doc(db, 'invoices', invoiceId);
    await updateDoc(invoiceRef, {
      ...updates
    });
    
    // Rebuild the revenues ledger when invoice is updated
    const invoice = await getInvoiceById(invoiceId);
    if (invoice) {
      await rebuildRevenuesLedger(invoice.clientId);
    }
  } catch (error) {
    throw new Error(`Error updating invoice: ${error}`);
  }
};

export const deleteInvoice = async (invoiceId: string): Promise<void> => {
  try {
    // Fetch invoice to capture clientId before deletion
    const existing = await getInvoiceById(invoiceId);
    const clientId = existing?.clientId;
    const invoiceRef = doc(db, 'invoices', invoiceId);
    await deleteDoc(invoiceRef);
    
    // Rebuild the revenues ledger after hard delete
    if (clientId) {
      await rebuildRevenuesLedger(clientId);
    }
  } catch (error) {
    throw new Error(`Error deleting invoice: ${error}`);
  }
};

export const getInvoices = async (): Promise<LogisticsInvoice[]> => {
  try {
    const querySnapshot = await getDocs(collection(db, 'invoices'));
    
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id, // This will now be the custom key (invoiceNumber-clientName)
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate()
    })) as LogisticsInvoice[];
    
    return result;
  } catch (error) {
    throw new Error(`Error getting invoices: ${error}`);
  }
};

export const getPendingInvoices = async (): Promise<LogisticsInvoice[]> => {
  try {
    const q = query(
      collection(db, 'invoices'),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(q);
    
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate()
    })) as LogisticsInvoice[];
    
    // Sort by createdAt in descending order (newest first)
    result.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    
    return result;
  } catch (error) {
    throw new Error(`Error getting pending invoices: ${error}`);
  }
};

export const getRejectedInvoicesByUser = async (userId: string): Promise<LogisticsInvoice[]> => {
  try {
    const q = query(
      collection(db, 'invoices'),
      where('status', '==', 'rejected'),
      where('createdBy', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate()
    })) as LogisticsInvoice[];
    // Sort newest first by createdAt
    result.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    return result;
  } catch (error) {
    throw new Error(`Error getting rejected invoices: ${error}`);
  }
};

export const getAllInvoicesByUser = async (userId: string): Promise<LogisticsInvoice[]> => {
  try {
    const q = query(
      collection(db, 'invoices'),
      where('createdBy', '==', userId)
    );
    const querySnapshot = await getDocs(q);
    
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate()
    })) as LogisticsInvoice[];
    
    // Filter out deleted invoices and sort by createdAt in descending order (newest first)
    const filteredResult = result.filter(invoice => invoice.status !== 'deleted');
    filteredResult.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    return filteredResult;
  } catch (error) {
    throw new Error(`Error getting all invoices: ${error}`);
  }
};

export const getInvoiceById = async (invoiceId: string): Promise<LogisticsInvoice | null> => {
  try {
    const docRef = doc(db, 'invoices', invoiceId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id, // This will be the custom key
        ...data,
        date: data.date?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        approvedAt: data.approvedAt?.toDate()
      } as LogisticsInvoice;
    }
    return null;
  } catch (error) {
    throw new Error(`Error getting invoice: ${error}`);
  }
};

// Helper function to get invoice by invoice number and client name
export const getInvoiceByNumberAndClient = async (invoiceNumber: string, clientName: string): Promise<LogisticsInvoice | null> => {
  try {
    // Since we now use invoice number as document ID, we can just use getInvoiceByNumber
    return await getInvoiceByNumber(invoiceNumber);
  } catch (error) {
    throw new Error(`Error getting invoice by number and client: ${error}`);
  }
};

// Helper function to get invoice by invoice number only
export const getInvoiceByNumber = async (invoiceNumber: string): Promise<LogisticsInvoice | null> => {
  try {
    const q = query(
      collection(db, 'invoices'),
      where('invoiceNumber', '==', invoiceNumber)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    return {
      id: doc.id,
      ...doc.data()
    } as LogisticsInvoice;
  } catch (error) {
    throw new Error(`Error getting invoice by number: ${error}`);
  }
};

export const getLatestRevenueInvoiceNumber = async (): Promise<string | null> => {
  try {
    const q = query(
      collection(db, 'invoices'),
      orderBy('invoiceNumber', 'desc'),
      limit(1)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    const data = snapshot.docs[0].data();
    return typeof data.invoiceNumber === 'string' ? data.invoiceNumber : null;
  } catch (error) {
    console.warn('Error fetching latest revenue invoice number:', error);
    return null;
  }
};

// Get invoices by client ID
export const getInvoicesByClientId = async (clientId: string): Promise<LogisticsInvoice[]> => {
  try {
    const q = query(
      collection(db, 'invoices'),
      where('clientId', '==', clientId),
      where('status', '==', 'approved')
    );
    const querySnapshot = await getDocs(q);
    const result = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate() || new Date(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
      approvedAt: doc.data().approvedAt?.toDate()
    })) as LogisticsInvoice[];
    
    // Sort by date in descending order after fetching
    result.sort((a, b) => b.date.getTime() - a.date.getTime());
    
    return result;
  } catch (error) {
    throw new Error(`Error getting client invoices: ${error}`);
  }
};

// Get latest invoice for a client (revenues)
export const getLatestClientInvoice = async (clientId: string): Promise<LogisticsInvoice | null> => {
  try {
    const q = query(
      collection(db, 'invoices'),
      where('clientId', '==', clientId),
      orderBy('createdAt', 'desc'),
      limit(1)
    );
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    const doc = querySnapshot.docs[0];
    const data = doc.data();
    return {
      id: doc.id,
      ...data,
      date: data.date?.toDate() || new Date(),
      createdAt: data.createdAt?.toDate() || new Date(),
      approvedAt: data.approvedAt?.toDate()
    } as LogisticsInvoice;
  } catch (error) {
    if (error instanceof Error && error.message.includes('index')) {
      // Firebase requires a composite index
    }
    return null;
  }
};
